<template>
    <div class="card-box m-b-10 p-10 relative card_color_left" v-if="show" :style="`border-left-color: ${getSegmentColor(item.segment)}`">
        <div class="table-box table_fixed table_deals">

            <!-- Infos -->
            <div class="table-detail w_50">
                <router-link :to="{ name: 'deals.show', params: { id: item.id } }" class="d_inline_b">
                    <span class="initial_name" v-pml-tooltip.top-right="item.member.full_name" v-if="item.member_id">{{ item.member.name_initials }}</span>
                    <span class="initial_name" v-pml-tooltip.top-right="$t('without_consultant')" v-if="!item.member_id"><i class="md-headset-mic"></i></span>
                </router-link>
            </div>
            <div class="table-detail">
                <router-link :to="{ name: 'deals.show', params: { id: item.id } }" class="m-t-0 m-b-5 f_size_14 bold show">#{{ item.code }}</router-link>
                <small class="c_default show"><b>{{ $t('created_at') }}:</b> {{ item.created_at | pmlDate('DD/MM/YYYY HH:mm') }}</small>
            </div>

            <!-- Follower -->
            <div class="table-detail t_center">
                <small class="text-muted" v-pml-tooltip="$t('follower')" v-if="item.follower_id">
                    <i class="fa fa-location-arrow text-dark m-r-5"></i>
                    {{ item.follower.full_name }}
                </small>
            </div>

            <!-- Source -->
            <div class="table-detail t_center">
                <span class="label label-default" :style="`background-color:${item.source.color}`" v-pml-tooltip="$t('source')" v-if="item.source">{{ item.source.name }}</span>
            </div>

            <!-- Perception -->
            <div class="table-detail table_divider_mobile t_center w_100">
                <span v-if="item.perception" class="label" :class="getPerceptionColor(item.perception)" v-pml-tooltip="$t('perception')">
                    {{ $t(item.perception) }}
                </span>
            </div>

            <!-- Actions -->
            <div class="table-detail table_divider_mobile table-actions-bar t_right w_100">
                <span v-if="item.status !== 'open'" class="status_deal" :class="{ 'sold': item.status === 'sold', 'lost': item.status === 'lost' }">{{ $t(item.status) }}</span>
                <router-link :to="{ name: 'deals.show', params: { id: item.id } }" class="btn btn-primary btn-xs" v-pml-tooltip="$t('show')"><i class="md md-visibility"></i></router-link>
                <span class="btn btn-danger btn-xs" v-if="member.is_admin" v-pml-tooltip="$t('delete')" @click="askToDelete(item)" v-pml-block="isDeleting"><i class="fa fa-times"></i></span>
            </div>

        </div>

        <!-- Pipeline -->
        <div v-if="item.pipeline_step_id" class="m-t-10 m-b-5">
            <p class="text-muted m-0">
                <span v-pml-tooltip.top-right="$t('pipeline')"><i class="fa fa-toggle-right text-dark m-r-5"></i></span>{{ item.step.pipeline.name }} <i class="fa fa-long-arrow-right"></i> {{ item.step.name }}
            </p>
            <div class="pipeline_line">
                <div class="pipeline_step" v-for="(step, index) in pipeline.steps" :class="{ 'step_completed': step.step < item.step.step, 'active': step.step === item.step.step }">
                    <div class="pipeline_circle" v-pml-tooltip="step.name"></div>
                </div>
            </div>
        </div>

        <hr class="m-10-0" v-else>

        <div class="table-box table_fixed">

            <!-- Task -->
            <div class="table-detail clearfix">
                <router-link :to="{ name: 'deals.show.tasks', params: { id: item.id } }" class="icon_list_deals" v-pml-tooltip.top-right="$t('pending_tasks')">
                    <i class="fa fa-calendar"></i>
                    <label class="badge badge-inverse">{{ countPendingTasks(item.tasks) }}</label>
                </router-link>
                <div v-if="countPendingTasks(item.tasks) > 0" class="p-r-10">
                    <small class="c_default show bold t_overflow">{{ getNextPending(item.tasks, 'due_date_at') | pmlDate('DD/MM/YYYY HH:mm') }}</small>
                    <small class="show t_overflow">{{ getNextPending(item.tasks, 'title') | pmlStripTag }}</small>
                </div>
                <div v-else class="p-r-10 m-t-10">
                    <small class="show t_overflow">{{ $t('there_is_no_pending_tasks') }}</small>
                </div>
            </div>

            <!-- Follow up -->
            <div class="table-detail clearfix">
                <router-link :to="{ name: 'deals.show.follow-ups', params: { id: item.id } }" class="icon_list_deals" v-pml-tooltip.top-right="$t('follow_ups')">
                    <i class="fa fa-history"></i>
                    <label class="badge badge-inverse">{{ item.follow_ups.length }}</label>
                </router-link>
                <div v-if="item.follow_ups.length > 0" class="p-r-10">
                    <small class="c_default show bold t_overflow">{{ getLastItem(item.follow_ups, 'created_at') | pmlDate('DD/MM/YYYY HH:mm') }}</small>
                    <small class="show t_overflow">{{ getLastItem(item.follow_ups, 'content') | pmlStripTag }}</small>
                </div>
                <div v-else class="p-r-10 m-t-10">
                    <small class="show t_overflow">{{ $t('there_is_no_follow_ups') }}</small>
                </div>
            </div>

            <!-- Email -->
            <div class="table-detail clearfix">
                <router-link :to="{ name: 'deals.show.emails', params: { id: item.id } }" class="icon_list_deals" v-pml-tooltip.top-right="$t('sent_emails')">
                    <i class="fa fa-envelope-o"></i>
                    <label class="badge badge-inverse">{{ item.emails.length }}</label>
                </router-link>
                <div v-if="item.emails.length > 0" class="p-r-10">
                    <small class="c_default show bold t_overflow">{{ getLastItem(item.emails, 'created_at') | pmlDate('DD/MM/YYYY HH:mm') }}</small>
                    <small class="show t_overflow">{{ getLastItem(item.emails, 'subject') | pmlStripTag }}</small>
                </div>
                <div v-else class="p-r-10 m-t-10">
                    <small class="show t_overflow">{{ $t('there_is_no_sent_emails') }}</small>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
    import Notify from 'utils/helpers/notify'
    import { trans } from 'utils/helpers/translation'
    import { last, isNil, filter, sortBy, first } from 'lodash'
    import { askToDelete } from 'utils/helpers/sweet-alert'
    import { destroy } from 'services/deals'
    import { findPipelineByStepId } from 'utils/helpers/find-store'
    import { mapGetters } from 'vuex'

    export default {
        name: 'Item',
        data () {
            return {
                show: true,
                isDeleting: false
            }
        },
        props: {
            item: {
                type: Object,
                default: () => {}
            }
        },
        computed: {
            ...mapGetters({
                'member': 'getMember'
            }),
            pipeline () {
                return findPipelineByStepId(this.item.step.id) || {}
            }
        },
        methods: {
            getLastItem (items, index) {
                let obj = last(items)
                return !isNil(obj) ? obj[index] : null
            },
            getNextPending (items, index) {
                if (isNil(items)) return

                let obj = filter(items, ['status', 'pending'])

                obj = sortBy(obj, ['due_date_at'])

                if (isNil(obj)) return

                return first(obj)[index]
            },
            countPendingTasks (items) {
                return filter(items, ['status', 'pending']).length
            },
            askToDelete (deal) {
                askToDelete()
                    .then((response) => (response.value) ? this.delete(deal) : null)
            },
            delete (deal) {
                this.isDeleting = true

                destroy(deal.id)
                    .then(() => {
                        Notify.success(trans('_notifications.success'))
                        this.$emit('deleted', deal.id)
                    })
                    .catch(error => Notify.error(trans(error.response.data.slug)))
                    .then(() => { this.isDeleting = false })
            },
            getSegmentColor (segment) {
                return segment ? segment.color : ''
            },
            getPerceptionColor (persption) {
                if (persption === 'hot') return 'label-danger'
                if (persption === 'cold') return 'label-primary'
                if (persption === 'warm') return 'label-warning'
                if (persption === 'no_perception') return 'label-light'
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/modules/leads/components/show/Deal.vue